import { ComboboxItemGroup } from '@mantine/core';
import { TreemapSeriesNodeItemOption } from '@vision/ui/components';
import { ReportTimeIntervalType, VisionPercentFormatType } from '@vision/ui/enums';
import i18n from '@vision/ui/i18n';
import {
  INode,
  ReportChartCompareTransform,
  ReportChartGroupCompare,
  ReportChartGroupCompareItemType,
  ReportChartGroupItemMeasure,
  ReportChartMeasureType,
  ReportChartTheme,
  ReportChartThemeDisplayFormat,
  ReportChartThemeLabelDisplayFormat,
  ReportChartThemeLabelPosition,
  ReportChartThemeRoundingMethod,
  ReportChartThemeSerieItem,
  ReportFeedbackNameType,
  ReportFeedbackResponse,
  ReportFeedbackResponseArrayDataType,
  ReportFeedbackResponseDataArray,
  ReportFeedbackResponseCollection,
  TreeSelectItemType,
  ReportsDetailGraphicSchemaValues,
  ReportFeedbackResponseStar,
  ReportFeedbackCxMetricsResponse,
  ReportFeedbackMatrixQuestionOverviewComparedData,
} from '@vision/ui/interfaces';
import { ALLOWED_FEEDBACK_COUNT_MEASURES } from '../constants/allowed-feedback-count-measures.constant';
import {
  convertArrayToGroupSelectItems,
  convertArrayToSelectItems,
  ensureArray,
  formatDate,
  formatNumber,
  formatPercent,
  getCookieLocalizationPercentFormat,
  getReportLocalizationKey,
  getValueByActiveLanguage,
  truncateString,
} from '@vision/ui/utils';
import dayjs from 'dayjs';
import { CallbackDataParams, TopLevelFormatterParams } from 'echarts/types/dist/shared';

const ALLOWED_FEEDBACK_COUNT_MEASURES_SET = new Set(ALLOWED_FEEDBACK_COUNT_MEASURES);
const MEASURES_WITH_OPTIONAL_PERCENT_SYMBOL = new Set<ReportChartMeasureType>([
  'feedback-csat',
  'feedback-nps',
  'feedback-ces',
  'feedback-weighted-score',
  'ticket-reopen-rate',
]);

export function getReportTableTextAlign(position: ReportChartThemeLabelPosition): 'center' | 'left' | 'right' {
  return position === 'left' || position === 'right' ? position : undefined;
}

function getReportChartAxisPercentageTotal(data: number[], totalFeedbackCount?: number): number {
  if (typeof totalFeedbackCount === 'number') {
    return totalFeedbackCount;
  }

  return data.reduce((acc, item) => acc + item, 0);
}

function isMeasureWithOptionalPercentSymbol(measure: ReportChartMeasureType): boolean {
  return MEASURES_WITH_OPTIONAL_PERCENT_SYMBOL.has(measure);
}

export function formatReportChartOptionalPercentSymbol(
  value: string,
  measure: ReportChartMeasureType,
  showPercentageSymbol?: boolean,
): string {
  const formattedValue = value.trim();

  if (!showPercentageSymbol || !isMeasureWithOptionalPercentSymbol(measure) || formattedValue.includes('%')) {
    return formattedValue;
  }

  if (getCookieLocalizationPercentFormat() === VisionPercentFormatType.COMMA_LEFT) {
    return `%${formattedValue}`;
  }

  return `${formattedValue}%`;
}

export function getReportDataName(name: ReportFeedbackNameType): string {
  if (typeof name === 'string' || typeof name === 'number') {
    return name.toString();
  }

  try {
    return getValueByActiveLanguage(name) || Object.values(name)[0] || '';
  } catch {
    return '';
  }
}

export function reportChartIsValidValue(value: ReportFeedbackNameType): boolean {
  return value !== null && value !== undefined && value !== '';
}

export function formatPieChartSeriesLabel(params: CallbackDataParams): string {
  const data = params.data as { valueLabel?: string };
  return data.valueLabel || params.name;
}

export function isNumericValue(value: unknown): boolean {
  if (typeof value === 'number') return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && !isNaN(Number(trimmed));
  }
  return false;
}

export function getRowCompareTypeForMultipleCompare(
  itemName: unknown,
  compares: ReportChartGroupCompareItemType[],
): ReportChartGroupCompareItemType {
  const [firstCompare] = compares;

  if (!isNumericValue(itemName)) {
    return firstCompare;
  }

  const timePeriodCompare = compares.find((compare) => compare.startsWith('time-period-'));
  return timePeriodCompare ?? firstCompare;
}

export function getColumnCompareTypeForMultipleCompare(
  columnValue: unknown,
  rowCompareType: ReportChartGroupCompareItemType,
  compares: ReportChartGroupCompareItemType[],
): ReportChartGroupCompareItemType {
  if (!isNumericValue(columnValue)) {
    const otherCompare = compares.find((compare) => compare !== rowCompareType);
    return otherCompare ?? compares[1] ?? compares[0];
  }

  const timePeriodCompares = compares.filter((compare) => compare.startsWith('time-period-'));
  const otherTimePeriodCompare = timePeriodCompares.find((compare) => compare !== rowCompareType);

  if (otherTimePeriodCompare) {
    return otherTimePeriodCompare;
  }

  const otherCompare = compares.find((compare) => compare !== rowCompareType);
  return otherCompare ?? compares[1] ?? compares[0];
}

export const createChannelSelectItems = (channelTypes: string[], nodes: INode[]): ComboboxItemGroup[] => {
  return channelTypes.map((channelType) => {
    const channels = nodes.filter((item) => item.type === channelType);
    return {
      group: i18n.t(`channelTypes.${channelType}`),
      items: convertArrayToSelectItems(channels, (item) => ({
        label: item.name,
        value: item.id,
      })),
    } as ComboboxItemGroup;
  });
};

//  burada compare değişince hangi değerlerin silineceğini belirliyoruz
export function getReportChartCompareKey(keyType: ReportChartGroupCompareItemType[]): string[] {
  return ReportChartCompareTransform.filter((item) => keyType.includes(item.keyType)).flatMap(
    (item) => item.compareKey,
  );
}

export function getReportChartCompareResetValues(
  keyTypes: ReportChartGroupCompareItemType[],
): Record<string, string[] | null> {
  return Object.fromEntries(
    getReportChartCompareKey(keyTypes).map((key): [string, string[] | null] => [
      key,
      key === 'selectedContentCategoryAiModelId' ? null : [],
    ]),
  );
}

function getLegendStatusByLabel(label: string, seriesItems: ReportChartThemeSerieItem[]): boolean {
  const item = seriesItems.find((color) => color.label === label);

  if (item?.hidden) {
    return false;
  }

  return true;
}

export function getLegendStatus(labels: string[], seriesItems: ReportChartThemeSerieItem[]): Record<string, boolean> {
  if (!seriesItems.length) {
    return {};
  }

  return labels.reduce(
    (acc, label) => {
      acc[label] = getLegendStatusByLabel(label, seriesItems);
      return acc;
    },
    {} as Record<string, boolean>,
  );
}

export function retrieveLegendStatus(
  labels: string[],
  seriesItems: ReportChartThemeSerieItem[],
): Record<string, boolean> {
  if (!seriesItems.length) {
    return {};
  }

  const legendStatus: Record<string, boolean> = {};

  labels.forEach((label) => {
    legendStatus[label] = getLegendStatusByLabel(label, seriesItems);
  });

  return legendStatus;
}

export function isReportFeedbackResponseDataArray(data: ReportFeedbackResponse[]): boolean {
  return ensureArray(data).some((item) => Array.isArray(item.data));
}

export function isReportFeedbackResponseDataNumber(data: ReportFeedbackResponse[]): boolean {
  return ensureArray(data).some((item) => typeof item.data === 'number');
}

export function isReportFeedbackResponseArrayDataTypeArray(
  data: unknown,
): data is ReportFeedbackResponseArrayDataType[] {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'name' in data[0] &&
    'data' in data[0] &&
    typeof (data[0] as Record<string, unknown>).name === 'string' &&
    Array.isArray((data[0] as Record<string, unknown>).data)
  );
}

export function isReportFeedbackResponseArrayDataTypeNumber(data: unknown): data is ReportFeedbackResponse {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'name' in data[0] &&
    'data' in data[0] &&
    typeof (data[0] as Record<string, unknown>).name === 'string' &&
    typeof (data[0] as Record<string, unknown>).data === 'number'
  );
}

export function isReportReportFeedbackResponse(data: any): data is ReportFeedbackResponse {
  return typeof data === 'object' && data !== null && 'name' in data && 'data' in data;
}

// isReportFeedbackResponseDataArray fonk buna göre düzelt
export function isReportFeedbackResponseDataArrayItem(
  data: ReportFeedbackResponse['data'],
): data is ReportFeedbackResponseDataArray {
  return Array.isArray(data);
}

export function secondsToHHMMSS(value: number): string {
  const hours = Math.floor(value / 3600);
  //  (Math/floor (/ x 60))
  const minutes = Math.floor((value - hours * 3600) / 60);
  // (Math/floor (- value (* (:data hours) 3600) (* (:data minutes) 60)))
  const seconds = Math.floor(value - hours * 3600 - minutes * 60);

  const hhMmSs = [
    { data: hours, trans: i18n.t('hours') },
    { data: minutes, trans: i18n.t('minutes') },
    { data: seconds, trans: i18n.t('seconds') },
  ].filter((val) => val.data > 0);

  return hhMmSs.map((val) => `${val.data} ${val.trans}`).join(' ');
}

export function reportChartDisplayDateFromInterval(value: number, interval: ReportTimeIntervalType): string {
  if (interval === ReportTimeIntervalType.MONTH) {
    return formatDate(value, 'monthYear', i18n.language).date;
  } else if (interval === ReportTimeIntervalType.QUARTER) {
    return formatDate(value, 'quarter', i18n.language).date;
  }

  return formatDate(value, 'full', i18n.language).date;
}

export function reportChartLabelFormatter(
  compare: ReportChartGroupCompareItemType,
  label: string,
  truncate: number = 30,
): string {
  try {
    let result = label.toString();
    if (!result) {
      return '';
    }

    switch (compare) {
      // If the comparison is by day, we return the day of the week.
      case 'time-period-by-day':
        result = dayjs.localeData().weekdays()[Number(result)];
        break;
      case 'time-period-by-month':
        // data is like January 1, February 2, March 3
        result = dayjs()
          .month(Number(result) - 1)
          .format('MMMM');
        break;
      case 'user-archiving':
      case 'user-assigned':
      case 'user-responsible':
      case 'user-best-assigned':
      case 'user-worst-assigned':
        result = result === ' ' ? i18n.t('unknownUser') : result;
        break;
      case 'question-title':
        result = getReportDataName(result);
        break;
    }

    if (truncate < 1) {
      return result;
    }
    if (!result) {
      return label;
    }
    return truncateString(result, truncate, '...');
  } catch (e) {
    console.error('reportChartLabelFormatter error', e, 'compare', compare, 'label', label);
    return label as string;
  }
}

export function reportChartDefaultDataLabelFormatter(
  measure: ReportChartMeasureType,
  label: string,
  maximumFractionDigits: number = 2,
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
  showPercentageSymbol: boolean = false,
): string {
  switch (measure) {
    case 'feedback-csat':
    case 'feedback-ces':
    case 'feedback-nps':
    case 'feedback-weighted-score':
    case 'ticket-reopen-rate':
      return formatReportChartOptionalPercentSymbol(
        formatReportChartNumberValue(Number(label), maximumFractionDigits, roundingMethod),
        measure,
        showPercentageSymbol,
      );
    case 'nps-kpi':
    case 'csat-kpi':
    case 'feedback-sentiment-score':
    case 'nps-realization-rate':
    case 'csat-realization-rate':
      return formatReportChartNumberValue(Number(label), maximumFractionDigits, roundingMethod);
    case 'action-time-archived':
    case 'action-time-completion':
    case 'action-time-internal-note':
    case 'action-time-read':
    case 'action-time-response':
    case 'ticket-average-time-to-first-resolution':
    case 'ticket-average-time-in-status':
    case 'ticket-average-time-to-latest-resolution':
      return secondsToHHMMSS(Number(label));
    default:
      return label;
  }
}

export function isNonDefaultReportChartRoundingMethod(roundingMethod?: ReportChartThemeRoundingMethod): boolean {
  return roundingMethod === 'roundUp' || roundingMethod === 'roundDown' || roundingMethod === 'round';
}

function applyReportChartRounding(value: number, roundingMethod: ReportChartThemeRoundingMethod): number {
  switch (roundingMethod) {
    case 'roundUp':
      return Math.ceil(value);
    case 'roundDown':
      return Math.floor(value);
    case 'round': {
      const roundedAbsoluteValue = Math.round(Math.abs(value));

      return roundedAbsoluteValue === 0 ? 0 : Math.sign(value) * roundedAbsoluteValue;
    }
    default:
      return value;
  }
}

function formatReportChartNumberValue(
  value: number,
  digits: number,
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
): string {
  if (isNonDefaultReportChartRoundingMethod(roundingMethod)) {
    return formatNumber(applyReportChartRounding(value, roundingMethod), {
      style: 'decimal',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  return formatNumber(value, {
    style: 'decimal',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

export function formatReportChartPercentDisplay(
  value: number,
  displayFormat: ReportChartThemeDisplayFormat = 'auto',
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
): string {
  if (isNonDefaultReportChartRoundingMethod(roundingMethod)) {
    return formatPercent(applyReportChartRounding(value, roundingMethod), {
      style: 'decimal',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  const digits = displayFormat === 'auto' ? 2 : (displayFormat as number);

  return formatPercent(value, {
    style: 'decimal',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

interface PieChartSeriesDataItem {
  name: string;
  value: number;
  legendName: string;
  valueLabel: string;
}

export function getPieChartSeriesData(
  filteredData: ReportFeedbackResponse[],
  measure: ReportChartMeasureType,
  labelDisplayFormat: ReportChartThemeLabelDisplayFormat,
  compare: ReportChartGroupCompareItemType[],
): PieChartSeriesDataItem[] {
  const total = filteredData.reduce((sum, item) => sum + (item.data as number), 0);

  return filteredData.map((item, index) => {
    const originalName = getReportDataName(item.name);
    const formattedName = reportChartLabelFormatter(compare[0], originalName);
    const value = item.data as number;
    const percent = total > 0 ? Math.round((value / total) * 100) : 0;

    let valueLabel = reportChartDefaultDataLabelFormatter(measure, value.toString());
    switch (labelDisplayFormat) {
      case 'number':
        valueLabel = reportChartDefaultDataLabelFormatter(measure, value.toString());
        break;
      case 'percentage':
        valueLabel = formatPercent(percent);
        break;
      case 'bothOfThem': {
        const val = reportChartDefaultDataLabelFormatter(measure, value.toString());
        valueLabel = `${val} (${formatPercent(percent)})`;
        break;
      }
      case 'categoryPercentage':
        valueLabel = formatPercent(percent);
        break;
    }

    return {
      name: `${formattedName}-${index}`,
      value,
      legendName: formattedName,
      valueLabel,
    };
  });
}

const reportChartLabelCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function compareReportChartLabels(a: string, b: string): number {
  const numericA = Number(a);
  const numericB = Number(b);
  const isNumericA = a.trim() !== '' && Number.isFinite(numericA);
  const isNumericB = b.trim() !== '' && Number.isFinite(numericB);

  if (isNumericA && isNumericB) {
    return numericA - numericB;
  }

  return reportChartLabelCollator.compare(a, b);
}

type ReportChartResultOrder = 'asc' | 'desc' | null;

export function reportChartSortSingleCompareData(
  data: ReportFeedbackResponse[],
  resultOrder?: ReportChartResultOrder,
): ReportFeedbackResponse[] {
  if (resultOrder || isReportFeedbackResponseDataArray(data)) {
    return data;
  }

  return [...data].sort((a, b) => compareReportChartLabels(getReportDataName(a.name), getReportDataName(b.name)));
}

export function reportChartSortLabels(
  labels: string[],
  compare?: ReportChartGroupCompareItemType,
  resultOrder?: ReportChartResultOrder,
): string[] {
  if (resultOrder) {
    return labels;
  }

  if (compare === 'time-period-by-day' || compare === 'time-period-by-month' || compare === 'time-period-by-hour') {
    return [...labels].sort((a, b) => Number(a) - Number(b));
  }

  return [...labels].sort(compareReportChartLabels);
}

type ExtractCountInput = number | ReportFeedbackResponseDataArray | ReportFeedbackMatrixQuestionOverviewComparedData;

function extractCountFromItemData(itemData: ExtractCountInput): number {
  if (typeof itemData === 'number') {
    return itemData;
  }
  if (Array.isArray(itemData)) {
    let sum = 0;
    for (const item of itemData) {
      if (Array.isArray(item) && item.length === 2 && typeof item[1] === 'number') {
        sum += item[1];
      }
    }
    return sum;
  }
  if (typeof itemData === 'object' && itemData !== null) {
    if ('feedback_count' in itemData) {
      return itemData.feedback_count;
    }
  }
  return 0;
}

function isReportFeedbackResponseStar(data: unknown): data is ReportFeedbackResponseStar {
  return (
    typeof data === 'object' &&
    data !== null &&
    'total_count' in data &&
    typeof (data as ReportFeedbackResponseStar).total_count === 'number'
  );
}

function isReportFeedbackCxMetricsResponse(data: unknown): data is ReportFeedbackCxMetricsResponse {
  return typeof data === 'object' && data !== null && 'feedback_count' in data;
}

export function calculateTotalFeedbackCount(data: ReportFeedbackResponseCollection): number {
  if (isReportFeedbackResponseStar(data)) {
    return data.total_count;
  }

  if (isReportFeedbackCxMetricsResponse(data)) {
    const feedbackCountData = data.feedback_count;
    if (Array.isArray(feedbackCountData)) {
      let total = 0;
      for (const item of feedbackCountData) {
        if ('data' in item && item.data) {
          total += extractCountFromItemData(item.data as ExtractCountInput);
        }
      }
      return total;
    }
  }

  let total = 0;

  if (Array.isArray(data)) {
    for (const item of data) {
      if (typeof item === 'object' && item !== null && 'data' in item) {
        total += extractCountFromItemData((item as { data: ExtractCountInput }).data);
      }
    }
    return total;
  }

  if (typeof data === 'object' && data !== null) {
    for (const key in data) {
      const value = (data as Record<string, unknown>)[key];
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'object' && item !== null && 'data' in item) {
            total += extractCountFromItemData((item as { data: ExtractCountInput }).data);
          }
        }
      }
    }
    return total;
  }

  return 0;
}

export function defaultReportChartNumberFormatter(value: number): string {
  return formatNumber(value, { style: 'decimal', maximumFractionDigits: 2 });
}

export function convertToSelectGroupItems(
  items: Array<ReportChartGroupItemMeasure | ReportChartGroupCompare>,
  groupTranslateKey: string,
  groupItemTranslateKey: string,
) {
  return convertArrayToGroupSelectItems(items, (item) => ({
    group: i18n.t(`page-reports:${groupTranslateKey}.${item.type}`),
    items: item.items.map((childItem) => ({
      label: i18n.t(`page-reports:${groupItemTranslateKey}.${getReportLocalizationKey(childItem)}`),
      value: childItem,
    })),
  }));
}

export function convertTreeSelectItemTypeToTreeSeriesData(data: TreeSelectItemType[]): TreemapSeriesNodeItemOption[] {
  return data.map((item) => {
    return {
      name: item.label,
      value: item.value,
      children: convertTreeSelectItemTypeToTreeSeriesData(item.children),
      data: item.data,
      extraData: item?.extraData,
    };
  });
}

export function getReportChartCompareAxisValue(
  compare: ReportChartGroupCompareItemType[],
  axis: 'x' | 'y' = 'x',
): ReportChartGroupCompareItemType {
  if (compare.length < 2) {
    return compare[0];
  }

  let valueX = compare[0];
  let valueY = compare[1];

  if (
    (compare.includes('time-period-by-hour') && compare.includes('time-period-by-day')) ||
    (compare.includes('time-period-by-hour') && compare.includes('time-period-by-month')) ||
    (compare.includes('time-period-by-day') && compare.includes('time-period-by-month'))
  ) {
    valueX = compare[0];
    valueY = compare[1];
  } else if (compare.includes('time-period-by-hour')) {
    valueX = 'time-period-by-hour';
    valueY = compare.filter((item) => item !== 'time-period-by-hour')[0];
  } else if (compare.includes('time-period-by-month')) {
    valueX = 'time-period-by-month';
    valueY = compare.filter((item) => item !== 'time-period-by-month')[0];
  } else if (compare.includes('time-period-by-day')) {
    valueX = 'time-period-by-day';
    valueY = compare.filter((item) => item !== 'time-period-by-day')[0];
  } else if (compare.includes('unit-name')) {
    valueX = 'unit-name';
    valueY = compare.filter((item) => item !== 'unit-name')[0];
  }

  if (axis === 'x') {
    return valueX;
  }

  return valueY;
}

export function reportChartFormatSecondsToHMS(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  // Saniyenin küsuratını iki basamakla sınırlama
  const formattedSecs = secs.toFixed(2).replace(/\.00$/, ''); // Tam sayı ise küsuratı kaldır

  // Saat ve dakika sıfırsa sadece saniyeyi döndür
  if (hours === 0 && minutes === 0) {
    return `${i18n.t('secondsShort', { value: +formattedSecs })}`;
  }

  // Sadece saat sıfırsa, dakikayı ve saniyeyi döndür
  if (hours === 0) {
    return `${i18n.t('minutesShort', { value: +minutes })} ${i18n.t('secondsShort', { value: +formattedSecs })}`;
  }

  // Saat ve dakika sıfır değilse, hepsini döndür
  return `${i18n.t('hoursShort', { value: +hours })} ${i18n.t('minutesShort', { value: +minutes })} ${i18n.t('secondsShort', { value: +formattedSecs })}`;
}

export function formatReportChartDataDisplay(
  value: number,
  measure: ReportChartMeasureType,
  displayFormat: ReportChartThemeDisplayFormat,
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
  showPercentageSymbol: boolean = false,
): string {
  switch (measure) {
    case 'action-time-archived':
    case 'action-time-completion':
    case 'action-time-internal-note':
    case 'action-time-read':
    case 'action-time-response':
    case 'ticket-average-time-to-first-resolution':
    case 'ticket-average-time-in-status':
    case 'ticket-average-time-to-latest-resolution':
      return reportChartFormatSecondsToHMS(value);
  }

  if (isNonDefaultReportChartRoundingMethod(roundingMethod)) {
    if (isMeasureWithOptionalPercentSymbol(measure)) {
      return reportChartDefaultDataLabelFormatter(measure, value.toString(), 0, roundingMethod, showPercentageSymbol);
    }

    return formatReportChartNumberValue(value, 0, roundingMethod);
  }

  if (isMeasureWithOptionalPercentSymbol(measure)) {
    const digit = displayFormat === 'auto' ? 2 : (displayFormat as number);

    return reportChartDefaultDataLabelFormatter(measure, value.toString(), digit, roundingMethod, showPercentageSymbol);
  }

  if (displayFormat === 'auto') {
    let digit = 2;
    switch (measure) {
      case 'feedback-count':
      case 'response-count':
      case 'response-ranking-overview':
      case 'customer-count':
      case 'matrix-question-csat-overview':
      case 'matrix-question-response-count':
      case 'ticket-count':
      case 'ticket-reopen-count':
        digit = 0;
        break;
      case 'nps-kpi':
      case 'csat-kpi':
      case 'feedback-sentiment-score':
        digit = 2;
        break;
      default:
        digit = 2;
        break;
    }

    return formatReportChartNumberValue(value, digit);
  }

  switch (measure) {
    case 'feedback-count':
      return formatReportChartNumberValue(value, 0);
    default:
      return formatReportChartNumberValue(value, displayFormat as number);
  }
}

export function formatReportChartAxisSimpleValue(value: number, displayFormat: ReportChartThemeDisplayFormat): string {
  if (displayFormat === 'auto') {
    return formatNumber(value, { style: 'decimal', maximumFractionDigits: 2 });
  }

  return formatNumber(value, {
    style: 'decimal',
    maximumFractionDigits: displayFormat as number,
    minimumFractionDigits: displayFormat as number,
  });
}

export function formatReportChartAxisPercentValue(
  value: number,
  selectedMeasure: ReportChartMeasureType,
  data: number[],
  displayFormat: ReportChartThemeDisplayFormat,
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
  totalFeedbackCount?: number,
): string {
  const total = getReportChartAxisPercentageTotal(data, totalFeedbackCount);
  const percentage = total === 0 ? 0 : (value / total) * 100;
  return formatReportChartPercentDisplay(percentage, displayFormat, roundingMethod);
}

interface BarChartAxisSeriesData {
  name: string;
  data: number[];
}

export function formatReportChartAxisCategoryPercentValue(
  value: number,
  data: number[],
  displayFormat: ReportChartThemeDisplayFormat,
  categoryIndex?: number,
  allSeriesData?: BarChartAxisSeriesData[],
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
): string {
  const valueIndex = categoryIndex !== undefined ? categoryIndex : data.indexOf(value);

  if (valueIndex === -1 || !allSeriesData) {
    const total = data.reduce((acc, item) => acc + item, 0);
    const percentage = total === 0 ? 0 : (value / total) * 100;
    return formatReportChartPercentDisplay(percentage, displayFormat, roundingMethod);
  }

  const categoryTotal = allSeriesData.reduce((sum, series) => {
    return sum + (series.data[valueIndex] || 0);
  }, 0);

  const percentage = categoryTotal === 0 ? 0 : (value / categoryTotal) * 100;
  return formatReportChartPercentDisplay(percentage, displayFormat, roundingMethod);
}

export function formatBarChartAxisValue(
  value: number,
  displayFormat: ReportChartThemeDisplayFormat,
  labelDisplayFormat: ReportChartThemeLabelDisplayFormat,
  selectedMeasure: ReportChartMeasureType,
  data: number[],
  roundingMethod?: ReportChartThemeRoundingMethod,
): string;

export function formatBarChartAxisValue(
  value: number,
  displayFormat: ReportChartThemeDisplayFormat,
  labelDisplayFormat: ReportChartThemeLabelDisplayFormat,
  selectedMeasure: ReportChartMeasureType,
  data: number[],
  categoryIndex?: number,
  allSeriesData?: BarChartAxisSeriesData[],
  roundingMethod?: ReportChartThemeRoundingMethod,
  showPercentageSymbol?: boolean,
  totalFeedbackCount?: number,
): string;

export function formatBarChartAxisValue(
  value: number,
  displayFormat: ReportChartThemeDisplayFormat,
  labelDisplayFormat: ReportChartThemeLabelDisplayFormat,
  selectedMeasure: ReportChartMeasureType,
  data: number[],
  categoryIndexOrRoundingMethod?: number | ReportChartThemeRoundingMethod,
  allSeriesData?: BarChartAxisSeriesData[],
  roundingMethod: ReportChartThemeRoundingMethod = 'default',
  showPercentageSymbol: boolean = false,
  totalFeedbackCount?: number,
): string {
  const categoryIndex = typeof categoryIndexOrRoundingMethod === 'number' ? categoryIndexOrRoundingMethod : undefined;
  const resolvedRoundingMethod =
    typeof categoryIndexOrRoundingMethod === 'string' ? categoryIndexOrRoundingMethod : roundingMethod;

  if (labelDisplayFormat === 'percentage') {
    return formatReportChartAxisPercentValue(
      value,
      selectedMeasure,
      data,
      displayFormat,
      resolvedRoundingMethod,
      totalFeedbackCount,
    );
  } else if (labelDisplayFormat === 'categoryPercentage') {
    return formatReportChartAxisCategoryPercentValue(
      value,
      data,
      displayFormat,
      categoryIndex,
      allSeriesData,
      resolvedRoundingMethod,
    );
  } else if (labelDisplayFormat === 'bothOfThem') {
    return `${formatReportChartDataDisplay(value, selectedMeasure, displayFormat, resolvedRoundingMethod)} (${formatReportChartAxisPercentValue(value, selectedMeasure, data, displayFormat, resolvedRoundingMethod, totalFeedbackCount)})`;
  }

  return formatReportChartDataDisplay(
    value,
    selectedMeasure,
    displayFormat,
    resolvedRoundingMethod,
    showPercentageSymbol,
  );
}

// If want to other charts, change name and use. because this is only for bar chart
export function formatMultipleOrSingleBarChartTooltip(
  seriesData: BarChartAxisSeriesData[],
  params: TopLevelFormatterParams,
  compare: ReportChartGroupCompareItemType[],
  selectedTheme: ReportChartTheme,
  selectedMeasure: ReportChartMeasureType,
  totalFeedbackCount?: number,
) {
  const tooltipData = params as (CallbackDataParams & { axisValue: number | string })[];
  const { axisValue, data, marker } = tooltipData[0];

  const isMultipleCompare = compare.length > 1;

  if (isMultipleCompare) {
    const items = tooltipData
      .filter((item) => item.data !== null)
      .map((item) => {
        const itemLabel = reportChartLabelFormatter(getReportChartCompareAxisValue(compare, 'x'), item.seriesName, -1);

        const itemDataFormatted = formatBarChartAxisValue(
          item.data as number,
          selectedTheme?.dataLabels?.displayFormat,
          selectedTheme?.dataLabels?.labelDisplayFormat,
          selectedMeasure,
          seriesData.find((seriesItem) => seriesItem.name === itemLabel)?.data as number[],
          item.dataIndex,
          seriesData,
          selectedTheme?.dataLabels?.roundingMethod,
          selectedTheme?.dataLabels?.showPercentageSymbol,
          totalFeedbackCount,
        );
        return `${item.marker} ${itemLabel} ${itemDataFormatted}`;
      })
      .join('<br />');

    return `${reportChartLabelFormatter(getReportChartCompareAxisValue(compare, 'y'), axisValue.toString(), 0)} <br/>${items}`;
  } else {
    const itemDataFormatted = formatBarChartAxisValue(
      data as number,
      selectedTheme?.dataLabels?.displayFormat,
      selectedTheme?.dataLabels?.labelDisplayFormat,
      selectedMeasure,
      seriesData[0].data as number[],
      tooltipData[0].dataIndex,
      seriesData,
      selectedTheme?.dataLabels?.roundingMethod,
      selectedTheme?.dataLabels?.showPercentageSymbol,
      totalFeedbackCount,
    );

    return `${marker} ${reportChartLabelFormatter(getReportChartCompareAxisValue(compare), axisValue.toString(), 0)} ${itemDataFormatted}`;
  }
}

export function isFeedbackCountAllowed(values: ReportsDetailGraphicSchemaValues): boolean {
  const { selectedMeasure, selectedMultipleMeasure, selectedLineMeasure, selectedBarMeasures } = values;

  const measuresToCheck = [
    selectedMeasure,
    ...(selectedMultipleMeasure || []),
    selectedLineMeasure,
    ...(selectedBarMeasures || []),
  ];

  return measuresToCheck.some((m) => m && ALLOWED_FEEDBACK_COUNT_MEASURES_SET.has(m));
}
